import React, { useMemo, useState } from "react";
import { Typography, TextField, Box, Autocomplete } from "@mui/material";
import { FormFieldContainer } from "components/ui/FormField";
import {
    autoCompleteStyles,
    FormContainer,
    inputStyles,
} from "components/ui/FormField/FormFieldContainer";
import AddressVerificationModal from "./partials/AddressVerificationModal";
import states from "constants/states";
import Page from "components/ui/Page/Page";
import { LoadingButton } from "@mui/lab";

/**
 * @description This component renders the "Shipping Address" step of the enrollment process, allowing users to input their shipping address and verify it before submission.
 * @param {Object} formData - The form data for the shipping address step.
 * @param {boolean} isSubmitting - Indicates if the form is currently being submitted.
 * @param {Function} onChange - The function to handle form field changes.
 * @param {Function} onSubmit - The function to handle form submission.
 * @param {Function} onVerifyShippingAddress - The function to handle shipping address verification.
 * @returns {JSX.Element} The rendered component.
 */
const ShippingAddress = ({
    formData,
    isSubmitting,
    onChange,
    onSubmit,
    onVerifyShippingAddress,
}) => {
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [suggestedAddress, setSuggestedAddress] = useState(null);
    const stateOptions = useMemo(
        () => states.map((state) => ({ label: state.name, value: state.value })),
        [],
    );

    const currentAddress = {
        name: `${formData.firstName} ${formData.lastName}`,
        primaryLine: formData.address1,
        secondaryLine: formData.address2,
        city: formData.city,
        state: formData?.state?.value,
        zipCode: formData.postalCode,
    };

    const setFormFieldValue = (name, value) => {
        onChange(name, value);
    };

    const handleInputEventChange = (e) => {
        const { name, value } = e.target;
        setFormFieldValue(name, value);
    };

    const handleVerifyShippingAddress = async () => {
        try {
            const suggested = await onVerifyShippingAddress();
            const address = await suggested.json();
            setSuggestedAddress(address);
        } catch {
        } finally {
            setShowAddressModal(true);
        }
    };

    const handleEditAddress = () => setShowAddressModal(false);

    const handleSubmit = (addressToUse) => {
        setShowAddressModal(false);
        onSubmit(addressToUse).catch((err) => {
            //console.log(err);
        });
    };

    const isSubmitEnabled =
        formData?.firstName?.trim?.() &&
        formData?.lastName?.trim?.() &&
        formData?.address1?.trim?.() &&
        formData?.city?.trim?.() &&
        formData?.state &&
        formData?.postalCode?.trim?.();

    return (
        <>
            <Page
                heading="Where should we send your Heart Health Kit?"
                subHeading="Every KardiaComplete member receives a Heart Health Kit, which includes a KardiaMobile 6L EKG device and an Omron Evolv blood pressure cuff."
                sx={
                    {
                        // "& .step-subheading": {
                        //     padding: 0,
                        // },
                    }
                }
            >
                <FormContainer
                    heading="Shipping Address"
                    sx={{
                        width: "65%",
                    }}
                >
                    <FormFieldContainer
                        heading="Full Name"
                        required
                        fields={[
                            <TextField
                                fullWidth
                                name="firstName"
                                placeholder="First Name"
                                value={formData.firstName}
                                //onChange={handleInputEventChange}
                                inputProps={{
                                    readOnly: true
                                }}
                                sx={{
                                    ...inputStyles(),
                                    pointerEvents: "none", // Disable interaction since this field is read-only.
                                }}
                            />,
                            <TextField
                                fullWidth
                                name="lastName"
                                placeholder="Last Name"
                                value={formData.lastName}
                                //onChange={handleInputEventChange}
                                inputProps={{
                                    readOnly: true
                                }}
                                sx={{
                                    ...inputStyles(),
                                    pointerEvents: "none", // Disable interaction since these field is read-only.
                                }}
                            />,
                        ]}
                    />
                    <FormFieldContainer
                        heading="Address Line 1"
                        subHeading="Please provide a residential address. We cannot ship to PO boxes"
                        required
                        fields={[
                            <TextField
                                fullWidth
                                name="address1"
                                placeholder="Address Line 1"
                                value={formData.address1}
                                onChange={handleInputEventChange}
                                sx={inputStyles()}
                            />,
                        ]}
                    />
                    <FormFieldContainer
                        heading="Address Line 2"
                        fields={[
                            <TextField
                                fullWidth
                                name="address2"
                                placeholder="Address Line 2"
                                value={formData.address2}
                                onChange={handleInputEventChange}
                                sx={inputStyles()}
                            />,
                        ]}
                    />

                    <FormFieldContainer
                        heading="City"
                        required
                        fields={[
                            <TextField
                                fullWidth
                                name="city"
                                placeholder="City"
                                value={formData.city}
                                onChange={handleInputEventChange}
                                sx={inputStyles()}
                            />,
                        ]}
                    />

                    <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                        <FormFieldContainer
                            heading="State"
                            required
                            fields={[
                                <Autocomplete
                                    name="state"
                                    options={stateOptions}
                                    value={formData.state}
                                    onChange={(event, newValue) => {
                                        setFormFieldValue("state", newValue);
                                    }}
                                    sx={autoCompleteStyles()}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="State" />
                                    )}
                                />,
                            ]}
                        />

                        <FormFieldContainer
                            heading="Postal Code"
                            required
                            fields={[
                                <TextField
                                    fullWidth
                                    name="postalCode"
                                    placeholder="Postal Code"
                                    value={formData.postalCode}
                                    onChange={handleInputEventChange}
                                    sx={inputStyles()}
                                />,
                            ]}
                        />
                    </Box>

                    <LoadingButton
                        variant="contained"
                        onClick={handleVerifyShippingAddress}
                        sx={{ mt: 3 }}
                        disabled={isSubmitting || !isSubmitEnabled}
                        loading={isSubmitting}
                    >
                        Submit
                    </LoadingButton>
                </FormContainer>
            </Page>
            <AddressVerificationModal
                open={showAddressModal}
                currentAddress={currentAddress}
                suggestedAddress={suggestedAddress}
                onContinue={handleSubmit}
                onEdit={handleEditAddress}
            />
        </>
    );
};

export default ShippingAddress;
